import { ChevronRight, X } from "lucide-react";
import { useEffect } from "react";

import { fonts, radius, shadow, tokens } from "../styles/theme";
import { SECONDARY_TABS, type TabId } from "./TabBar";

const DESCRIPTIONS: Record<TabId, string> = {
  today: "",
  trays: "",
  deliveries: "",
  inventory: "",
  calendar: "Zoom-out planning horizon",
  plans: "Planting templates",
  yield: "Harvest analytics",
  reference: "Crop spec lookup",
};

export function MoreSheet({
  open,
  activeTab,
  onClose,
  onPick,
}: {
  open: boolean;
  activeTab: TabId;
  onClose: () => void;
  onPick: (t: TabId) => void;
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 30,
        pointerEvents: open ? "auto" : "none",
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(43, 42, 38, 0.32)",
          opacity: open ? 1 : 0,
          transition: "opacity 0.18s ease",
        }}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label="More"
        style={{
          position: "absolute",
          left: "12px",
          right: "12px",
          bottom: "12px",
          maxWidth: "520px",
          margin: "0 auto",
          paddingBottom: "env(safe-area-inset-bottom)",
          background: tokens.card,
          border: `1px solid ${tokens.line}`,
          borderRadius: radius.xl,
          boxShadow: shadow.lg,
          transform: open ? "translateY(0)" : "translateY(16px)",
          opacity: open ? 1 : 0,
          transition: "transform 0.22s ease, opacity 0.18s ease",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 16px 10px",
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <div
            style={{
              fontSize: "18px",
              color: tokens.ink,
              letterSpacing: "-0.01em",
            }}
          >
            More
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: "44px",
              height: "44px",
              border: "none",
              borderRadius: radius.pill,
              background: tokens.sunken,
              color: tokens.inkSoft,
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
            }}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: "6px",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          }}
        >
          {SECONDARY_TABS.map((t) => {
            const active = activeTab === t.id;
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => {
                    onPick(t.id);
                    onClose();
                  }}
                  aria-current={active ? "page" : undefined}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "14px 14px",
                    border: "none",
                    borderRadius: radius.md,
                    background: active ? tokens.sageSoft : "transparent",
                    color: tokens.ink,
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: fonts.body,
                    fontSize: "15px",
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: radius.sm,
                      background: active ? tokens.sage : tokens.sunken,
                      color: active ? "#fff" : tokens.inkSoft,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <t.Icon width={18} height={18} strokeWidth={1.9} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontWeight: 600,
                        color: tokens.ink,
                      }}
                    >
                      {t.label}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: "12px",
                        color: tokens.muted,
                        marginTop: "2px",
                      }}
                    >
                      {DESCRIPTIONS[t.id]}
                    </span>
                  </span>
                  <ChevronRight size={16} color={tokens.faint} strokeWidth={2} />
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
